import type { Translations } from './types'

/* ── Spanish (default) ───────────────────────────────────────── */
export const es: Translations = {
  profile: {
    title:         'Mi perfil',
    backBtn:       '← Inicio',
    statsTitle:    'Mis estadísticas',
    sessionsTitle: 'Actividad reciente',
    accountTitle:  'Mi cuenta',
    editName:      'Editar nombre',
    saveName:      'Guardar',
    cancelEdit:    'Cancelar',
    tierLabel:     'Plan',
    emailLabel:    'Correo',
    memberSince:   'Miembro desde',
    noSessions:    'Aún no has jugado ninguna partida.',
    completed:     'Completado',
    abandoned:     'Abandonado',
    score:         'pts',
    moves:         'turnos',
    viewPlans:     'Ver planes y precios',
    tierNames:      { BASIC: 'Básico', PLUS: 'Plus', PREMIUM: 'Premium' },
    languageTitle:  'Idioma',
    languageEs:     'Español',
    languageEn:     'English',
    professionalModeTitle:      'Modo Profesional',
    professionalModeActive:     'El modo profesional está activo en tu cuenta.',
    disableProfessional:        'Desactivar modo profesional',
    disableProfessionalHint:    'Se desactivará el acceso a perfiles y rutinas de pacientes.',
    professionalModeTeaser:     'Modo Profesional 👩‍⚕️',
    professionalModeTeaserDesc: 'Con el Modo Profesional puedes crear perfiles individuales para tus pacientes o familiares, diseñar rutinas personalizadas y hacer seguimiento de su evolución. Disponible en el plan Premium.',
    professionalModeUpgradeBtn: 'Ver plan Premium',
    statsChartsTitle: 'Mis Estadísticas',
    helpTitle:        '¿Necesitas ayuda?',
    helpName:         'Nombre',
    helpMessage:      'Mensaje',
    helpSubmit:       'Enviar mensaje',
    helpSending:      'Enviando…',
    helpSuccess:      '✅ Mensaje enviado. Te responderemos pronto.',
    helpError:        'No se pudo enviar el mensaje. Inténtalo de nuevo.',
  },
  auth: {
    loginTab:         'Iniciar sesión',
    signupTab:        'Crear cuenta',
    loginWelcome:     'Bienvenido/a de nuevo',
    loginSubtitle:    'Continúa con tus ejercicios',
    signupWelcome:    'Únete a Tengo de TO',
    signupSubtitle:   'Crea tu cuenta gratuita',
    nameLabel:        'Nombre',
    namePlaceholder:  'Tu nombre',
    emailLabel:       'Correo electrónico',
    emailPlaceholder: 'tu@correo.com',
    passwordLabel:    'Contraseña',
    passwordPlaceholder: '••••••••',
    loginBtn:         'Entrar',
    signupBtn:        'Crear cuenta',
    loggingIn:        'Entrando…',
    signingUp:        'Creando cuenta…',
    switchToSignup:   '¿No tienes cuenta? Regístrate',
    switchToLogin:    '¿Ya tienes cuenta? Inicia sesión',
    errInvalidCreds:  'Correo o contraseña incorrectos',
    errEmailExists:   'Este correo ya está registrado',
    errPasswordMin:   'La contraseña debe tener al menos 8 caracteres',
    errGeneric:       'Algo salió mal. Inténtalo de nuevo.',
    errNotVerified:   'Debes verificar tu correo antes de entrar. Revisa tu bandeja de entrada.',
    logout:           'Salir',
    verifyTitle:      '¡Revisa tu correo!',
    verifySubtitle:   'Te hemos enviado un enlace de verificación. Haz clic en él para activar tu cuenta.',
    verifyResend:     'Reenviar enlace',
    verifyResendSent: 'Enlace reenviado. Revisa tu bandeja.',
    verifyResendWait: 'Espera un momento antes de reenviar.',
    forgotLink:       '¿Olvidaste tu contraseña?',
    forgotTitle:      'Recuperar contraseña',
    forgotSubtitle:   'Escribe tu correo y te enviaremos un enlace para restablecer tu contraseña.',
    forgotBtn:        'Enviar enlace',
    forgotSending:    'Enviando…',
    forgotSent:       'Si el correo está registrado, recibirás un enlace en breve.',
    resetTitle:       'Nueva contraseña',
    resetSubtitle:    'Elige una contraseña segura para tu cuenta.',
    resetNewPassword: 'Nueva contraseña',
    resetConfirm:     'Confirmar contraseña',
    resetBtn:         'Restablecer contraseña',
    resetSaving:      'Guardando…',
    resetSuccess:     'Contraseña restablecida. Ya puedes iniciar sesión.',
    resetMismatch:    'Las contraseñas no coinciden.',
    resetExpired:     'El enlace ha caducado o no es válido. Solicita uno nuevo.',
    backToLogin:      'Volver al inicio de sesión',
    termsAccept:      'He leído y acepto los',
    termsLink:        'Términos y Condiciones',
    termsRequired:    'Debes aceptar los Términos y Condiciones para continuar.',
    showPassword:     'Mostrar contraseña',
    hidePassword:     'Ocultar contraseña',
  },
  app: {
    name: 'Tengo de TO',
    tagline: 'Ejercicios para una mente activa y feliz',
    footer: 'Tengo de TO · Estimulación cognitiva',
  },
  dashboard: {
    stats: { today: 'Sesiones hoy', streak: 'Racha semanal', best: 'Mejor marca', percentile: 'Posición' },
    activitiesTitle: 'Actividades',
    comingSoon: 'Pronto',
    downloadsAriaLabel: 'Ver descargas',
    downloadsBtnText:   'Actividades para imprimir',
    activitiesGrid:     'Actividades disponibles',
    games: {
      memory:       { label: 'Memory',          description: 'Encuentra pares de cartas. Entrena tu memoria visual y concentración.' },
      wordmatch:    { label: 'Palabras',         description: 'Imágenes y definiciones para enriquecer tu vocabulario.' },
      cutlery:      { label: 'Cajón',            description: 'Ordena los cubiertos en su sitio. Ejercita el orden y la clasificación.' },
      wordsearch:   { label: 'Sopa de Letras',   description: 'Encuentra las palabras escondidas en la cuadrícula. Entrena tu atención.' },
      sudoku:       { label: 'Sudoku',           description: 'Rellena la cuadrícula con números. Ejercita la lógica y la concentración.' },
      maths:        { label: 'Matemáticas',      description: 'Resuelve operaciones de suma, resta, multiplicación y división.' },
      shoppinglist: { label: 'Lista de la Compra', description: 'Memoriza la lista y encuentra los productos en el supermercado.' },
      eurmoney:     { label: 'El dinero',          description: 'Aprende a reconocer monedas y billetes y practica el manejo del dinero.' },
      pillbox:      { label: 'El Pastillero',      description: 'Lee la guía del médico y organiza correctamente las pastillas en cada compartimento.' },
      attention:    { label: 'Atención',           description: 'Lee las reglas y marca las palabras de la nube: rodea, subraya o tacha.' },
      symmetry:     { label: 'Simetría',            description: 'Completa la mitad que falta del dibujo de píxeles para crear una imagen simétrica.' },
      whats_missing: { label: '¿Qué falta?',        description: 'Compara dos grupos de imágenes y encuentra las que no aparecen en el otro grupo.' },
      roman:        { label: 'Números Romanos',     description: 'Lee el número y escoge o escribe su equivalente en numeración romana.' },
      crosswords:   { label: 'Crucigrama',           description: 'Une las letras del círculo para formar palabras y completar el crucigrama.' },
      hours:        { label: 'El Reloj',             description: 'Lee la hora en relojes analógicos y digitales y exprésala en palabras.' },
      synonyms:     { label: 'Sinónimos',            description: 'Encuentra el sinónimo o antónimo de cada palabra.' },
      puzzle:       { label: 'Puzle',               description: 'Une las piezas de la imagen. Entrena tu percepción visual y memoria.' },
      chess:        { label: 'Ajedrez',             description: 'Reconoce las piezas, aprende sus movimientos y resuelve puzzles de ajedrez.' },
      numtarget:    { label: 'Número Mágico',        description: 'Usa las fichas de números y operaciones para llegar al número objetivo.' },
    },
  },
  shoppinglist: {
    title:                 'Lista de la Compra',
    subtitle:              'Memoriza la lista y escoge los productos correctos',
    backToDashboard:       '← Inicio',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    loading:               'Cargando…',
    selected:              'elegidos',
    time:                  'tiempo',
    memoriseTitle:         'Tu lista de la compra',
    memoriseHint:          'Recuerda bien estos productos antes de ir al supermercado',
    readyBtn:              '¡Ya lo tengo! →',
    cartTitle:             'Ya tienes esto en el carro',
    optionsTitle:          '¿Qué te falta comprar?',
    instructionPick:       'Selecciona los {n} productos que te faltan',
    checkBtn:              'Comprobar →',
    previewListTitle:      'Lista',
    previewCartTitle:      'Supermercado',
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Lista de 5 · 2 faltan · 5 opciones' },
      medium: { label: 'Medio',   hint: 'Lista de 7 · 3 faltan · 7 opciones' },
      hard:   { label: 'Difícil', hint: 'Lista de 9 · 4 faltan · 9 opciones' },
    },
    complete: {
      title:         '¡Muy bien!',
      subtitle:      'Acertaste {correct} de {total} productos',
      accuracyLabel: 'Precisión',
      timeLabel:     'Tiempo',
      levelLabel:    'Nivel',
      playAgain:     'Jugar de nuevo',
      changeLevel:   'Cambiar nivel',
    },
  },
  eurmoney: {
    title:                 'El dinero',
    subtitle:              'Practica el manejo de monedas y billetes en euros',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    loading:               'Cargando…',
    noValues:              'Las imágenes del tema no tienen valor asignado. Asígnales valor en el panel de administración.',
    moves:                 'jugadas',
    time:                  'tiempo',
    round:                 'pregunta',
    modes: {
      identify: { label: '¿Qué moneda es?',   description: 'Mira la imagen e identifica la moneda o billete' },
      count:    { label: '¿Cuánto hay?',       description: 'Suma el valor de todas las monedas y billetes' },
      pay:      { label: 'Paga el precio',     description: 'Añade monedas y billetes al plato hasta llegar al precio' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '5 preguntas · cantidades pequeñas' },
      medium: { label: 'Medio',   hint: '7 preguntas · hasta 5,00 €' },
      hard:   { label: 'Difícil', hint: '10 preguntas · hasta 20,00 €' },
    },
    instructionIdentify:   '¿Cuánto vale esta moneda o billete?',
    instructionCount:      'Suma el valor de todas las monedas y billetes',
    instructionPay:        'Añade monedas al plato para pagar el precio exacto',
    instructionPayOver:    '¡Te has pasado! Quita alguna moneda',
    instructionPayCorrect: '¡Correcto! Eso es exactamente el precio',
    dishLabel:             'Tu plato',
    dishTotal:             'Total',
    clearBtn:              'Vaciar',
    complete: {
      title:        '¡Enhorabuena!',
      subtitle:     'Acertaste {correct} de {total} preguntas',
      correctLabel: 'Aciertos',
      movesLabel:   'Jugadas',
      timeLabel:    'Tiempo',
      levelLabel:   'Nivel',
      playAgain:    'Jugar de nuevo',
      changeLevel:  'Cambiar nivel',
      home:         '← Inicio',
    },
  },

  pillbox: {
    title:                 'El Pastillero',
    subtitle:              'Organiza las pastillas según la guía del médico',
    backToDashboard:       '← Inicio',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'acciones',
    time:                  'tiempo',
    guidelineTitle:        'Guía del médico',
    pillboxTitle:          'Pastillero',
    bottlesTitle:          'Frascos',
    columnMed:             'Medicamento',
    columnPill:            '💊',
    instructionSelect:     'Toca un frasco para seleccionarlo',
    instructionPlace:      'Toca un compartimento para añadir una pastilla',
    checkBtn:              'Comprobar →',
    wrongFeedback:         'Algunos compartimentos no están bien. ¡Inténtalo de nuevo!',
    schedules: {
      breakfast: 'Desayuno',
      lunch:     'Comida',
      merienda:  'Merienda',
      dinner:    'Cena',
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '2 medicamentos · sin frascos extra' },
      medium: { label: 'Medio',   hint: '3 medicamentos · 1 frasco de distracción' },
      hard:   { label: 'Difícil', hint: '4 medicamentos · 2 frascos de distracción' },
    },
    complete: {
      title:           '¡Perfecto!',
      subtitle:        'Has preparado el pastillero correctamente',
      movesLabel:      'Acciones',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  attention: {
    title:                 'Atención',
    subtitle:              'Lee las reglas y marca las palabras correctas',
    subtitleNumbers:       'Lee las reglas y marca los números correctos',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige modo',
    modeWords:             'Palabras',
    modeNumbers:           'Números',
    chooseThemesTitle:     'Añadir temas (opcional)',
    chooseThemesHint:      'Activa temas para incluir sus palabras y condiciones de categoría',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'toques',
    time:                  'tiempo',
    checkBtn:              'Comprobar →',
    instructionPlay:       'Toca cada palabra para ciclar entre círculo, subrayado y tachado',
    instructionPlayNumbers:'Toca cada número para ciclar entre círculo, subrayado y tachado',
    previewHint:           'Rodea, subraya o tacha según las reglas',
    conditions: {
      is_odd:         'Impares',
      is_even:        'Pares',
      gt:             'Mayores que {x}',
      lt:             'Menores que {x}',
      digit_sum_gt:   'Suma de cifras mayor que {y}',
      digit_count_eq: 'De {n} cifras',
      digit_count_lt: 'Menos de {n} cifras',
      digit_count_gt: 'Más de {n} cifras',
      multiple_of:    'Múltiplos de {x}',
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '20 elementos · 1 regla' },
      medium: { label: 'Medio',   hint: '40 elementos · 2 reglas' },
      hard:   { label: 'Difícil', hint: '60 elementos · 3 reglas' },
    },
    complete: {
      title:         '¡Muy bien!',
      subtitle:      'Has completado el ejercicio de atención',
      accuracyLabel: 'Precisión',
      movesLabel:    'Toques',
      timeLabel:     'Tiempo',
      levelLabel:    'Nivel',
      playAgain:     'Jugar de nuevo',
      changeLevel:   'Cambiar nivel',
    },
  },

  symmetry: {
    title:                          'Simetría',
    subtitle:                       'Completa el dibujo de píxeles',
    backToDashboard:                '← Inicio',
    chooseTypeTitle:                'Elige el tipo',
    chooseModeTitle:                'Elige el eje',
    chooseDifficultyTitle:          'Elige la dificultad',
    startBtn:                       'Empezar →',
    moves:                          'toques',
    time:                           'tiempo',
    progress:                       'celdas correctas',
    instructionPaint:               'Selecciona un color y toca las celdas para completar la simetría',
    instructionComplete:            '¡Simetría completada!',
    instructionPaintTranslation:    'Selecciona un color y toca las celdas para continuar el patrón',
    instructionCompleteTranslation: '¡Patrón completado!',
    eraser:                         'Borrador',
    colorPalette:                   'Color',
    types: {
      symmetry:    { label: 'Simetría 🪞',     description: 'Refleja el patrón respecto a un eje' },
      translation: { label: 'Traslación →',   description: 'Copia el patrón a la otra mitad' },
    },
    modes: {
      vertical:                 { label: 'Vertical ↔',   description: 'Completa la mitad derecha' },
      horizontal:               { label: 'Horizontal ↕', description: 'Completa la mitad inferior' },
      quad:                     { label: 'Cuádruple ✦',  description: 'Completa los tres cuartos restantes' },
      'translation-vertical':   { label: 'Vertical →',   description: 'Copia el patrón a la mitad derecha' },
      'translation-horizontal': { label: 'Horizontal ↓', description: 'Copia el patrón a la mitad inferior' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Cuadrícula 8×8 · solo negro' },
      medium: { label: 'Medio',   hint: 'Cuadrícula 12×12 · 4 colores' },
      hard:   { label: 'Difícil', hint: 'Cuadrícula 16×16 · 6 colores' },
    },
    complete: {
      title:           '¡Perfecto!',
      subtitle:        'Has completado la simetría',
      movesLabel:      'Toques',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  whatsMissing: {
    title:                 '¿Qué falta?',
    subtitle:              'Encuentra las diferencias entre los dos grupos',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige el tipo de imágenes',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'toques',
    time:                  'tiempo',
    instructionEasy:       'Toca los {n} elementos del Grupo A que no están en el Grupo B',
    instructionBidi:       'Quedan {n} elementos únicos por encontrar en ambos grupos',
    panelSelect:           'selecciona aquí',
    panelRef:              'referencia',
    modes: {
      emoji:  { label: 'Emojis',  description: 'Imágenes y símbolos' },
      shapes: { label: 'Formas',  description: 'Figuras de colores' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '10 elementos · 3 diferencias · un sentido' },
      medium: { label: 'Medio',   hint: '15 elementos · 5 diferencias · ambos grupos' },
      hard:   { label: 'Difícil', hint: '20 elementos · 7 diferencias · ambos grupos' },
    },
    complete: {
      title:           '¡Excelente!',
      subtitle:        'Has encontrado todas las diferencias',
      movesLabel:      'Toques',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      foundLabel:      'Encontradas',
      playAgain:       'Volver a jugar',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  hours: {
    title:                 'El Reloj',
    subtitle:              'Lee la hora en el reloj y exprésala en palabras',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    reverseLabel:          'Modo inverso',
    reverseHint:           'Te dan la hora escrita y tú eliges o pones el reloj',
    startBtn:              'Empezar →',
    question:              'pregunta',
    moves:                 'intentos',
    instructionRead:       '¿Qué hora marca el reloj?',
    instructionType:       'Escribe la hora que ves en el reloj',
    instructionReverse:    '¿Qué reloj marca esta hora?',
    instructionSet:        'Pon el reloj a la hora indicada',
    exampleHint:           'Formato',
    inputPlaceholder:      'las tres y cuarto',
    submitBtn:             'Comprobar →',
    dragHint:              'Arrastra las manecillas H (hora) y M (minutos)',
    spinnerHint:           'Usa las flechas para poner la hora',
    previewNormal:         '¿Qué hora es?',
    previewReverse:        'Elige el reloj correcto',
    previewReverseWritten: 'las tres y cuarto',
    mode: {
      analog:  { label: 'Analógico', description: 'Reloj de agujas clásico' },
      digital: { label: 'Digital',   description: 'Pantalla de horas y minutos (24h)' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '{rounds} preguntas · {options} opciones' },
      medium: { label: 'Medio',   hint: '{rounds} preguntas · {options} opciones' },
      hard:   { label: 'Difícil', hint: '{rounds} preguntas · escribe la respuesta' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        '¡Has completado el ejercicio del reloj!',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
    time: {
      hourNames: ['', 'una', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez', 'once', 'doce'],
      articleSg: 'la',
      articlePl: 'las',
      minuteTemplates: {
        0:  '{article} {hour} en punto',
        5:  '{article} {hour} y cinco',
        10: '{article} {hour} y diez',
        15: '{article} {hour} y cuarto',
        20: '{article} {hour} y veinte',
        25: '{article} {hour} y veinticinco',
        30: '{article} {hour} y media',
        35: '{article} {nextHour} menos veinticinco',
        40: '{article} {nextHour} menos veinte',
        45: '{article} {nextHour} menos cuarto',
        50: '{article} {nextHour} menos diez',
        55: '{article} {nextHour} menos cinco',
      },
    },
  },

  crosswords: {
    title:                 'Crucigrama',
    subtitle:              'Une las letras para completar el crucigrama',
    backToDashboard:       '← Inicio',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    loading:               'Cargando…',
    generationFailed:      'No se pudo generar el crucigrama. Inténtalo de nuevo.',
    wordsFound:            'palabras',
    time:                  'tiempo',
    instructionDrag:       'Arrastra las letras para formar palabras',
    instructionComplete:   '¡Crucigrama completado!',
    hintBtn:               'Pista',
    bonusWordsLabel:       'Palabras extra',
    bonusWordFlash:        '¡Palabra extra!',
    difficulty: {
      easy:   { label: 'Fácil',   hint: '3 palabras cortas' },
      medium: { label: 'Medio',   hint: '5 palabras' },
      hard:   { label: 'Difícil', hint: '7 palabras largas' },
    },
    complete: {
      title:               '¡Crucigrama completado!',
      subtitle:            '¡Has encontrado todas las palabras!',
      movesLabel:          'Palabras formadas',
      timeLabel:           'Tiempo',
      levelLabel:          'Nivel',
      hintsUsedLabel:      'Pistas usadas',
      bonusWordsLabel:     'Palabras extra',
      playAgain:           'Jugar de nuevo',
      changeLevel:         'Cambiar nivel',
      backToDashboard:     '← Inicio',
    },
  },

  roman: {
    title:                 'Números Romanos',
    subtitle:              'Traduce el número a numeración romana',
    backToDashboard:       '← Inicio',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'intentos',
    time:                  'tiempo',
    questionCounter:       'Pregunta {n} de {total}',
    questionPrompt:        '¿Cómo se escribe en romano?',
    questionPromptHard:    '¿Qué número representa este numeral romano?',
    typeAnswer:            'Escribe el número…',
    submitBtn:             'Enviar',
    wrongFeedback:         'Incorrecto, inténtalo de nuevo',
    hardHint:              'Escribe el número arábigo (ej. V̄ = 5000)',
    previewLabel:          'Escoge el número romano correcto',
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Números del 1 al 20 · 4 opciones' },
      medium: { label: 'Medio',   hint: 'Números del 1 al 1000 · 6 opciones' },
      hard:   { label: 'Difícil', hint: 'Números del 1 al 10 000 · escribe el número arábigo' },
    },
    complete: {
      title:           '¡Excelente!',
      subtitle:        'Has completado los números romanos',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  synonyms: {
    title:                 'Sinónimos y Antónimos',
    subtitle:              'Enriquece tu vocabulario',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    loading:               'Cargando palabras…',
    loadError:             'No se pudieron cargar las palabras. Inténtalo de nuevo.',
    moves:                 'intentos',
    time:                  'tiempo',
    questionCounter:       'Pregunta {n} de {total}',
    questionPrompt:        'Sinónimo de:',
    questionPromptAntonym: 'Antónimo de:',
    skipBtn:               'Saltar →',
    selectAllCorrect:      'Selecciona todas las respuestas correctas',
    mode: {
      synonyms: { label: 'Sinónimos', description: 'Encuentra el sinónimo de cada palabra' },
      antonyms: { label: 'Antónimos', description: 'Encuentra el antónimo de cada palabra' },
      mixed:    { label: 'Mixto',     description: 'Mezcla de sinónimos y antónimos' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '4 opciones, una correcta' },
      medium: { label: 'Medio',   hint: 'Empareja 10 palabras con su respuesta' },
      hard:   { label: 'Difícil', hint: '10 opciones, varias correctas' },
    },
    complete: {
      title:           '¡Excelente!',
      subtitle:        '¡Vocabulario completado!',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  puzzle: {
    title:                 'Puzle',
    subtitle:              'Une las piezas y completa la imagen',
    backToDashboard:       '← Inicio',
    chooseThemeTitle:      'Elige un tema',
    chooseThemeHint:       'Selecciona un tema para empezar',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'intentos',
    time:                  'tiempo',
    round:                 'Imagen',
    pairs:                 'Pares',
    instructionPick:       '¿Qué pieza completa la imagen?',
    instructionPairs:      'Toca las dos mitades que forman la misma imagen',
    modes: {
      puzzle: { label: 'Puzle',  description: 'Encuentra la pieza que falta' },
      pairs:  { label: 'Pares',  description: 'Une las mitades de cada imagen' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '2 piezas · 3 opciones' },
      medium: { label: 'Medio',   hint: '4 piezas · 6 opciones' },
      hard:   { label: 'Difícil', hint: '6 piezas · 10 opciones' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        '¡Puzle completado!',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  chess: {
    title:                 'Ajedrez',
    subtitle:              'Aprende y practica ajedrez',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'movimientos',
    time:                  'tiempo',
    round:                 'ronda',
    modes: {
      recognize: { label: 'Reconocer piezas', description: 'Identifica las piezas del ajedrez' },
      movement:  { label: 'Movimientos',      description: 'Marca todos los escaques posibles de cada pieza' },
      capture:   { label: 'Captura',          description: 'Captura todos los peones en el menor número de movimientos' },
      puzzle:    { label: 'Puzzles',          description: 'Resuelve puzzles clásicos de ajedrez' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '4 piezas · posiciones sencillas' },
      medium: { label: 'Medio',   hint: '6 piezas · tablero completo' },
      hard:   { label: 'Difícil', hint: 'Todas las piezas · retos avanzados' },
    },
    recognize: {
      instruction: '¿Qué pieza es esta?',
      correct:     'correctas',
      colorWhite:  'Blancas',
      colorBlack:  'Negras',
      pieces: {
        k: 'Rey',
        q: 'Dama',
        r: 'Torre',
        b: 'Alfil',
        n: 'Caballo',
        p: 'Peón',
      },
    },
    movement: {
      instruction:  'Toca todos los escaques donde puede moverse {piece}',
      correct:      'correctas',
      wrong:        'Hay {n} escaques válidos — ¡inténtalo de nuevo!',
      submitBtn:    'Comprobar',
      nextBtn:      'Siguiente →',
      finish:       'Finalizar →',
    },
    capture: {
      instruction:    'Selecciona tu pieza y captura todos los peones',
      movesLabel:     'movimientos',
      capturedLabel:  'capturadas',
      optimalLabel:   'Objetivo: {n} movimientos',
      yourMovesLabel: 'Tus movimientos',
      bestLabel:      'Óptimo',
      nextBtn:        'Siguiente →',
      finishBtn:      'Finalizar →',
    },
    puzzle: {
      instruction:  'Encuentra la mejor jugada para las {color}',
      loading:      'Cargando puzzle…',
      loadError:    'No se pudo cargar el puzzle. Comprueba tu conexión.',
      solvedLabel:  'resueltos',
      solved:       '¡Correcto! ♟',
      failed:       'Movimiento incorrecto — prueba de nuevo.',
      thinking:     'El rival está pensando…',
      white:        'blancas',
      black:        'negras',
      nextBtn:      'Siguiente puzzle →',
      finishBtn:    'Finalizar →',
      retryBtn:     'Reintentar',
    },
    complete: {
      title:           '¡Bien jugado!',
      subtitle:        '¡Partida completada!',
      movesLabel:      'Movimientos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  numtarget: {
    title:                 'Número Mágico',
    subtitle:              'Usa las fichas para llegar al número objetivo · ¡cuantas más uses, mejor puntuación!',
    backToDashboard:       '← Inicio',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    goalLabel:             'Objetivo',
    roundCounter:          'Ronda {n} de {total}',
    instruction:           'Toca fichas y operaciones para construir el cálculo · ¡más fichas, mejor puntuación!',
    submitBtn:             'Comprobar',
    clearBtn:              'Borrar todo',
    deleteBtn:             '⌫',
    resultLabel:           'Resultado:',
    cardsUsed:             'Fichas usadas',
    attemptsLabel:         'Intentos',
    ops: {
      add:        '+',
      sub:        '−',
      mul:        '×',
      div:        '÷',
      parenOpen:  '(',
      parenClose: ')',
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '4 fichas · solo sumas y restas' },
      medium: { label: 'Medio',   hint: '6 fichas · suma, resta y multiplica' },
      hard:   { label: 'Difícil', hint: '7 fichas · todas las operaciones' },
    },
    complete: {
      title:           '¡Bravo!',
      subtitle:        '¡Llegaste al número objetivo!',
      cardsUsedLabel:  'Fichas usadas',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      attemptsLabel:   'Intentos',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  memory: {
    title: 'Memory',
    subtitle: 'Encuentra los pares de cartas',
    backToDashboard: '← Inicio',
    backToSetup: '←',
    chooseThemeTitle: 'Elige un tema',
    classicTheme: 'Clásico',
    chooseModeTitle: 'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn: 'Empezar →',
    moves: 'turnos',
    time: 'tiempo',
    modes: {
      'images-images': { label: 'Imágenes ↔ Imágenes', description: 'Empareja dos imágenes iguales' },
      'words-words':   { label: 'Palabras ↔ Palabras', description: 'Empareja dos palabras iguales' },
      'images-words':  { label: 'Imágenes ↔ Palabras', description: 'Empareja una imagen con su nombre' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '6 pares · 12 cartas' },
      medium: { label: 'Medio',   hint: '8 pares · 16 cartas' },
      hard:   { label: 'Difícil', hint: '12 pares · 24 cartas' },
    },
    complete: {
      title: '¡Muy bien!',
      subtitle: 'Encontraste todos los pares',
      movesLabel: 'Turnos',
      timeLabel: 'Tiempo',
      levelLabel: 'Nivel',
      playAgain: 'Jugar de nuevo',
      changeMode: 'Cambiar modo',
      backToDashboard: '← Inicio',
    },
  },
  cutlery: {
    title:                 'Cubiertos',
    subtitle:              'Ordena o pon la mesa',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'intentos',
    time:                  'tiempo',
    instructionPick:       'Selecciona un cubierto',
    instructionPlace:      'Ahora colócalo en su compartimento',
    instructionPickTable:  'Selecciona un cubierto',
    instructionPlaceTable: 'Colócalo en el sitio correcto',
    menuTitle:             'Menú del comensal',
    closeMenuBtn:          'Cerrar',
    slots: {
      spoon:    'Cucharas',
      fork:     'Tenedores',
      knife:    'Cuchillos',
      teaspoon: 'Cucharillas',
    },
    comandaTitle:         'Comanda de la mesa',
    drawerTapInstruction: 'Toca el cajón para sacar · Toca el cubierto para devolver',
    checkBtn:             'Comprobar →',
    countWrongFeedback:   'Hay errores, revisa las cantidades e inténtalo de nuevo',
    modes: {
      drawer: { label: 'Ordenar el cajón',    description: 'Clasifica los cubiertos en sus compartimentos' },
      table:  { label: 'Poner la mesa',       description: 'Lee el menú y coloca los cubiertos correctos' },
      count:  { label: 'Cuántos cubiertos',   description: 'Cuenta los cubiertos que necesita la mesa' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Poca variedad' },
      medium: { label: 'Medio',   hint: 'Más complejidad' },
      hard:   { label: 'Difícil', hint: 'Cucharilla incluida' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        'Has colocado todos los cubiertos',
      subtitleTable:   'Has puesto la mesa correctamente',
      subtitleCount:   'Has contado los cubiertos correctamente',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  wordmatch: {
    title:                 'Palabras',
    subtitle:              'Pon a prueba tu vocabulario con imágenes, definiciones y anagramas',
    backToDashboard:       '← Inicio',
    chooseThemeTitle:      'Elige un tema',
    classicTheme:          'Clásico',
    chooseModeTitle:       'Elige el modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    moves:                 'intentos',
    time:                  'tiempo',
    questionCounter:       'Pregunta {n} de {total}',
    questionPrompt:        '¿Cuál es la palabra?',
    anagramLabel:          'Ordena las letras',
    hintLetterCount:       'Número de letras',
    hintFirstLetter:       'Primera letra',
    letters:               'letras',
    typeAnswer:            'Escribe tu respuesta...',
    submitBtn:             'Confirmar',
    wrongFeedback:         'Incorrecto, inténtalo de nuevo',
    mode: {
      images:      { label: 'Imágenes',      description: 'Observa la imagen y elige la palabra' },
      definitions: { label: 'Definiciones',  description: 'Lee la definición y elige la palabra' },
      anagram:     { label: 'Anagrama',      description: 'Ordena las letras para encontrar la palabra' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: '4 opciones' },
      medium: { label: 'Medio',   hint: '6 opciones' },
      hard:   { label: 'Difícil', hint: 'Escribe la respuesta' },
    },
    complete: {
      title:           '¡Excelente!',
      subtitle:        'Has completado todas las preguntas',
      movesLabel:      'Intentos',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar dificultad',
      backToDashboard: '← Inicio',
    },
  },

  wordsearch: {
    title:                 'Sopa de Letras',
    subtitle:              'Encuentra todas las palabras escondidas',
    backToDashboard:       '← Inicio',
    chooseThemeTitle:      'Elige un tema',
    classicTheme:          'Clásico',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    wordsFound:            'palabras',
    time:                  'tiempo',
    instructionStart:      'Toca las letras una a una para seleccionar una palabra',
    instructionContinue:   'Sigue en línea recta, o toca otra letra para empezar de nuevo',
    modes: {
      simple:    { label: 'Horizontal y vertical', description: 'Las palabras van en fila o en columna' },
      diagonals: { label: 'Con diagonales',        description: 'También pueden ir en diagonal' },
      reversed:  { label: 'Al revés',              description: 'Las palabras pueden ir en cualquier dirección, incluso al revés' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Cuadrícula 8×8 · 5 palabras' },
      medium: { label: 'Medio',   hint: 'Cuadrícula 12×12 · 8 palabras' },
      hard:   { label: 'Difícil', hint: 'Cuadrícula 16×16 · 12 palabras' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        'Has encontrado todas las palabras',
      wordsLabel:      'Palabras',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  sudoku: {
    title:                 'Sudoku',
    subtitle:              'Rellena la cuadrícula sin repetir ningún número',
    backToDashboard:       '← Inicio',
    chooseModeTitle:       'Elige un modo',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    errors:                'errores',
    time:                  'tiempo',
    notes:                 'Notas',
    erase:                 'Borrar',
    instructionSelect:     'Toca una celda vacía para seleccionarla',
    instructionEnter:      'Toca un número para colocarlo en la celda',
    modes: {
      standard: { label: 'Clásico 9×9', description: 'La cuadrícula de sudoku de toda la vida' },
      mini:     { label: 'Mini 4×4',    description: 'Versión más sencilla con números del 1 al 4' },
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Muchas pistas' },
      medium: { label: 'Medio',   hint: 'Pistas moderadas' },
      hard:   { label: 'Difícil', hint: 'Pocas pistas' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        'Has completado el sudoku',
      movesLabel:      'Movimientos',
      errorsLabel:     'Errores',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  maths: {
    title:                 'Matemáticas',
    subtitle:              'Resuelve operaciones matemáticas básicas',
    backToDashboard:       '← Inicio',
    chooseOpsTitle:        'Elige las operaciones',
    chooseOpsHint:         'Puedes combinar varias a la vez',
    chooseDifficultyTitle: 'Elige la dificultad',
    startBtn:              'Empezar →',
    correct:               'correctas',
    time:                  'tiempo',
    questionCounter:       'Pregunta {n} de {total}',
    instructionPick:       '¿Cuál es el resultado?',
    instructionCorrect:    '¡Correcto!',
    operations: {
      addition:       'Suma',
      subtraction:    'Resta',
      multiplication: 'Multiplicación',
      division:       'División',
    },
    difficulty: {
      easy:   { label: 'Fácil',   hint: 'Números hasta el 10 · 8 preguntas' },
      medium: { label: 'Medio',   hint: 'Números hasta el 20 · 10 preguntas' },
      hard:   { label: 'Difícil', hint: 'Números hasta el 50 · 12 preguntas' },
    },
    complete: {
      title:           '¡Muy bien!',
      subtitle:        'Has completado las matemáticas',
      correctLabel:    'Respuestas',
      timeLabel:       'Tiempo',
      levelLabel:      'Nivel',
      playAgain:       'Jugar de nuevo',
      changeLevel:     'Cambiar nivel',
      backToDashboard: '← Inicio',
    },
  },

  subscription: {
    title:       'Planes',
    subtitle:    'Elige el plan que mejor se adapte a ti',
    backBtn:         '← Volver',
    currentPlan:     'Tu plan actual',
    includedPlan:    'Incluido en tu plan',
    upgradeBtn:      'Elegir plan',
    manageBtn:       'Gestionar suscripción',
    loadingCheckout: 'Redirigiendo a pago…',
    successBanner:   '¡Suscripción activada! Bienvenido a tu nuevo plan.',
    canceledBanner:  'Pago cancelado. Tu plan no ha cambiado.',
    errorBanner:     'Algo salió mal. Inténtalo de nuevo.',
    tiers: {
      basic: {
        name:        'Básico',
        price:       'Gratis',
        period:      '',
        description: 'Todo lo que necesitas para empezar a entrenar tu mente.',
        features:    ['Actividades básicas 🧠', 'Partidas ilimitadas', 'Estadísticas de progreso'],
      },
      plus: {
        name:        'Plus',
        price:       '3,90 €',
        period:      '/ mes',
        description: 'Más actividades para mantener tu mente activa.',
        features:    ['Todo lo del plan Básico', 'Actividades funcionales 🛠️', 'Actividades imprimibles 🖨️', 'Cuadernos de actividades 📚'],
      },
      premium: {
        name:        'Premium',
        price:       '5,90 €',
        period:      '/ mes',
        description: 'La experiencia completa para el bienestar cognitivo.',
        features:    ['Todo lo del plan Plus', 'Cuadernos personalizados 📓', 'Modo Profesional 👩‍⚕️', 'Acceso anticipado a novedades'],
      },
    },
  },

  downloads: {
    title:           'Descargas',
    intro:           'Descarga actividades en PDF para imprimir y disfrutar sin pantallas.',
    printablesTab:   'Imprimibles',
    notebooksTab:    'Cuadernos',
    upgradeBtn:      '🔒 Mejorar suscripción',
    notReady:        'Próximamente',
    downloading:     'Descargando…',
    downloadBtn:     '⬇ Descargar PDF',
    loading:         'Cargando…',
    emptyPrintables:       'Pronto habrá actividades para descargar.',
    emptyNotebooks:        'Pronto habrá cuadernos para descargar.',
    notebookMeta:          'Cuaderno · {n} actividades',
    errorDownload:         'Error al descargar',
    myNotebooksTitle:      'Mis cuadernos',
    libraryTitle:          'Biblioteca',
    emptyMyNotebooks:      'Aún no tienes cuadernos generados.',
    generateNotebookBtn:   'Generar mi cuaderno',
    premiumRequired:       'Suscríbete a Premium para generar tus propios cuadernos.',
    upgradeToPremiumBtn:   'Ver planes Premium',
    builderTitle:          'Crear mi cuaderno',
    builderActivities:     'Actividades',
    builderAddActivity:    'Añadir actividad',
    builderSelectActivity: 'Selecciona una actividad',
    builderAddWordsearch:  'Sopa de letras',
    builderAddSudoku:      'Sudoku',
    builderAddMaths:       'Matemáticas',
    builderAddAttention:      'Atención',
    builderAddSymmetry:       'Simetría',
    builderAddWhatsMissing:   '¿Qué falta?',
    builderAddRoman:           'Números Romanos',
    builderDifficultyLabel:    'Dificultad',
    builderPagesLabel:         'Páginas',
    builderAddHours:           '¿Qué hora es?',
    builderAddSynonyms:        'Sinónimos y Antónimos',
    builderAddPuzzle:          'Puzle (unir mitades)',
    builderCountLabel:         'Cantidad',
    builderSearchModeLabel:    'Modo de búsqueda',
    builderGridLabel:          'Cuadrícula',
    builderModeLabel:          'Modo',
    builderOperationLabel:     'Operación',
    builderTypeLabel:          'Tipo',
    builderClockTypeLabel:     'Tipo de reloj',
    builderWriteModeLabel:     'Modo de escritura',
    builderSymmetryModeLabel:  'Simetría',
    builderThemesOptionalLabel: 'Temas (opcional, todos si no se selecciona ninguno)',
    builderNoThemesMsg:        'Sin temas — se usarán palabras por defecto',
    builderDiffEasy:           'Fácil',
    builderDiffMedium:         'Medio',
    builderDiffHard:           'Difícil',
    builderWsModeSimple:       'Simple (→ ↓)',
    builderWsModeDiagonals:    'Diagonales',
    builderWsModeAllDirs:      'Todas las direcciones',
    builderSdModeClassic:      'Clásico 9×9',
    builderSdModeMini:         'Mini 4×4',
    builderMtOpAddition:       'Suma (+)',
    builderMtOpSubtraction:    'Resta (−)',
    builderMtOpMultiplication: 'Multiplicación (×)',
    builderMtOpDivision:       'División (÷)',
    builderAtModeWords:        'Palabras',
    builderAtModeNumbers:      'Números',
    builderSymModeVertical:    'Vertical ↔',
    builderSymModeHorizontal:  'Horizontal ↕',
    builderSymModeQuadrant:    'Cuadrante ✦',
    builderWmModeEmojis:       'Emojis',
    builderWmModeShapes:       'Formas',
    builderHrsModeJoin:        'Unir columnas',
    builderHrsModeWrite:       'Escribir / Dibujar',
    builderHrsClockAnalog:     'Analógico',
    builderHrsClockDigital:    'Digital',
    builderHrsClockMixed:      'Mixto',
    builderHrsWriteWords:      'Escribir la hora con palabras',
    builderHrsWriteDraw:       'Dibujar las agujas',
    builderSynModeSynonyms:    'Sinónimos',
    builderSynModeAntonyms:    'Antónimos',
    builderSynModeMixed:       'Mixto',
    builderSynPrintWrite:      'Escribir',
    builderSynPrintJoin:       'Unir columnas',
    builderRomPrintWrite:      'Escribir',
    builderRomPrintJoin:       'Unir columnas',
    builderGenerate:       'Generar cuaderno',
    builderGenerating:     'Generando…',
    statusGenerating:      'Generando PDF…',
    statusFailed:          'Error al generar',
    retryBtn:              'Reintentar',
    deleteBtn:             'Eliminar',
    typeLabels: {
      memory_cards:     'Cartas de memoria',
      memory_reference: 'Tabla imagen-palabra',
      definitions:      'Tarjetas de definiciones',
    },
    tierLabels: {
      BASIC:   'Básico',
      PLUS:    'Plus',
      PREMIUM: 'Premium',
    },
    diffLabels: {
      easy:   'Fácil',
      medium: 'Media',
      hard:   'Difícil',
    },
  },

  adminHub: {
    title:             'Administración',
    back:              '← Inicio',
    backToAdmin:       '← Admin',
    themesTitle:       'Temas',
    themesDesc:        'Gestionar temas de imágenes y generar contenido con IA',
    usersTitle:        'Usuarios',
    usersDesc:         'Ver usuarios, buscar por email y gestionar suscripciones',
    printablesTitle:   'Imprimibles',
    printablesDesc:    'Crear y gestionar imprimibles y cuadernos de actividades',
    activitiesTitle:   'Actividades',
    activitiesDesc:    'Gestionar el tier y la visibilidad de cada actividad',
    statsTitle:        'Estadísticas',
    statsDesc:         'Uso de actividades, sesiones y cuadernos generados',
  },

  adminStats: {
    title:             'Estadísticas',
    back:              '← Admin',
    totalUsers:        'Usuarios totales',
    activeThisWeek:    'Activos esta semana',
    totalSessions:     'Sesiones totales',
    sessionsThisWeek:  'Sesiones esta semana',
    sessionsByActivity:'Sesiones por actividad',
    sessionsByCategory:'Sesiones por categoría',
    abandonRate:       'Tasa de abandono',
    avgDuration:       'Duración media por actividad',
    notebooksByActivity: 'Cuadernos por actividad',
    topThemes:         'Temas más usados',
    noData:            'Sin datos',
    abandonRateHeader: 'Actividad',
    avgDurationHeader: 'Actividad',
    themeHeader:       'Tema',
    countHeader:       'Cantidad',
    activityHeader:    'Actividad',
    categoryHeader:    'Categoría',
  },

  professional: {
    hubTitle:           'Modo Profesional',
    hubSubtitle:        'Gestiona perfiles, crea rutinas de actividades y sigue la evolución de cada paciente.',
    enableBtn:          'Activar modo profesional',
    enableDesc:         'El modo profesional te permite crear perfiles de pacientes y configurar secuencias de actividades personalizadas.',
    premiumRequired:    'Requiere suscripción Premium',
    premiumRequiredDesc:'El modo profesional está disponible exclusivamente para usuarios con suscripción Premium.',
    goToPremium:        'Ver planes',
    back:               '← Inicio',

    profilesTitle:      'Perfiles',
    newProfile:         'Nuevo perfil',
    noProfiles:         'Aún no tienes perfiles. Crea uno para empezar.',

    profileName:        'Nombre del paciente',
    profileNotes:       'Notas (opcional)',
    profilePin:         'PIN (4 dígitos)',
    profilePinHint:     'El profesional introducirá este PIN al finalizar cada sesión.',
    createProfile:      'Crear perfil',
    editProfile:        'Editar perfil',
    saveProfile:        'Guardar',
    deleteProfile:      'Eliminar perfil',
    confirmDelete:      '¿Seguro que quieres eliminar este perfil? Se borrarán todas sus rutinas y sesiones.',
    cancel:             'Cancelar',

    playbooksTitle:     'Rutinas',
    newPlaybook:        'Nueva rutina',
    noPlaybooks:        'Este perfil no tiene rutinas todavía.',
    playbookName:       'Nombre de la rutina',
    createPlaybook:     'Crear rutina',
    editPlaybook:       'Renombrar',
    savePlaybook:       'Guardar',
    deletePlaybook:     'Eliminar rutina',
    startSession:       'Iniciar sesión',

    editorTitle:        'Editar rutina',
    addActivity:        'Añadir actividad',
    noItems:            'Esta rutina está vacía. Añade actividades para empezar.',
    saveChanges:        'Guardar cambios',
    difficulty:         'Dificultad',
    difficultyEasy:     'Fácil',
    difficultyMedium:   'Media',
    difficultyHard:     'Difícil',
    theme:              'Tema',
    themeClassic:       'Clásico',
    themeNone:          'Sin tema',

    sessionProgress:    'Actividad {current} de {total}',
    sessionNext:        'Siguiente →',
    sessionFinish:      'Finalizar sesión',
    sessionLocked:      '¡Sesión completada!',
    sessionLockedDesc:  'Todas las actividades han terminado. El profesional debe introducir el PIN para continuar.',
    sessionPinPrompt:   'Introduce el PIN',
    sessionUnlock:      'Desbloquear',
    sessionWrongPin:    'PIN incorrecto. Inténtalo de nuevo.',
    exitTitle:          'Salir de la sesión',
    exitDesc:           'Introduce el PIN del perfil para salir.',
    exitCancel:         'Volver a la actividad',

    resultsTitle:    '¡Actividad completada!',
    resultsDuration: 'Tiempo',
    resultsScore:    'Aciertos',
    resultsMoves:    'Movimientos',
    resultsNext:     'Siguiente actividad',

    statsTitle:         'Estadísticas',
    statsFilter:        'Filtrar por perfil',
    statsAllProfiles:   'Todos los perfiles',
    statsTotalSessions: 'Sesiones totales',
    statsLastSession:   'Última sesión',
    statsNoData:        'Aún no hay datos para este perfil.',
    statsScore:         'Puntuación',
    statsDuration:      'Duración (s)',
    statsDate:          'Fecha',
    statsScoreChart:    'Puntuación por sesión',
    statsDurationChart: 'Duración por sesión',
    statsShowTable:     'Ver tabla',
    statsShowChart:     'Ver gráfico',
    statsActivities:    'Actividades',
  },

  items: [
    { id: 1,  emoji: '🌸', word: 'Flor de cerezo', definition: 'Hermosa flor que florece en primavera' },
    { id: 2,  emoji: '🦋', word: 'Mariposa',        definition: 'Insecto con grandes alas de vivos colores' },
    { id: 3,  emoji: '🌊', word: 'Ola',             definition: 'Cresta de agua en movimiento en el mar' },
    { id: 4,  emoji: '🏔️', word: 'Montaña',         definition: 'Gran elevación natural del terreno' },
    { id: 5,  emoji: '🌙', word: 'Luna',            definition: 'Satélite natural de la Tierra, visible de noche' },
    { id: 6,  emoji: '⭐', word: 'Estrella',        definition: 'Cuerpo luminoso de gas visible en el cielo nocturno' },
    { id: 7,  emoji: '🌿', word: 'Helecho',         definition: 'Planta sin flores con hojas verdes y plumosas' },
    { id: 8,  emoji: '🦅', word: 'Águila',          definition: 'Gran ave rapaz de enorme poder y envergadura' },
    { id: 9,  emoji: '🐠', word: 'Pez payaso',      definition: 'Pequeño pez colorido que habita entre anémonas' },
    { id: 10, emoji: '🌺', word: 'Hibisco',         definition: 'Arbusto tropical de grandes flores llamativas' },
    { id: 11, emoji: '🎵', word: 'Melodía',         definition: 'Sucesión de notas musicales que forman una canción' },
    { id: 12, emoji: '📖', word: 'Historia',        definition: 'Relato de eventos, reales o imaginarios' },
  ],
  terms: {
    title:   'Términos y Condiciones',
    backBtn: 'Volver',
  },
  common: {
    saveError:             'No se pudo guardar tu progreso. Comprueba tu conexión.',
    dismissError:          'Cerrar',
    resumeTitle:           '¿Continuar tu último juego?',
    sessionResumeTitle:    'Sesión en progreso',
    sessionResumeDesc:     'Tienes una sesión en progreso. ¿Deseas continuar donde lo dejaste?',
    sessionResumeContinue: 'Continuar',
    sessionResumeNew:      'Empezar de nuevo',
  },
  stats: {
    title:          'Mis estadísticas',
    avgScore:       'Puntuación media',
    bestActivity:   'Mejor actividad',
    totalSessions:  'Partidas jugadas',
    radarTitle:     'Rendimiento por categoría',
    radarAriaLabel: 'Gráfico radial de rendimiento por categoría',
    evolutionTitle: 'Evolución de la puntuación',
    durationTitle:  'Tiempo dedicado por actividad',
    noData:         'Juega algunas partidas para ver tus estadísticas aquí.',
    viewStats:      'Ver estadísticas detalladas →',
    thisWeek:       'Esta semana',
    lastWeek:       'Semana pasada',
    filterBy:       'Panel de filtros',
    dateRange:      'Rango de fechas',
    activities:     'Actividades',
    categoriesLabel: 'Categorías',
    allActivities:  'Todas las actividades',
    allCategories:  'Todas las categorías',
    datePresets: {
      today:          'Hoy',
      'this-week':   'Esta semana',
      'this-month':  'Este mes',
      'last-3-months': 'Últimos 3 meses',
      'last-6-months': 'Últimos 6 meses',
      'this-year':   'Este año',
      'all-time':    'Todo',
    },
    categories: {
      memory:           'Memoria',
      language:         'Lenguaje',
      'daily-life':     'Vida diaria',
      logic:            'Lógica',
      maths:            'Matemáticas',
      attention:        'Atención',
      'visual-spatial': 'Visoespacial',
    },
  },
}
